import {
  useCreateMultiplesampleHistory,
  useDeleteMultiplesampleHistory,
  useEditMultiplesampleHistory,
  useGetHistoryOfExemplaire,
  useGetHistoryOfExemplaireMerged,
  useReorderMultiplesampleProvenance,
  useUpdateMultiplesampleHistorySequence,
  type MultiplesampleHistory,
  type MultiplesampleHistoryMergedAuction,
} from "@/api/multiplesample-history.api";
import * as AF from "@/components/form-items/AbstractForm";
import {
  MaskableTableSection,
  MaskableTableSectionBody,
  MaskableTableSectionHeader,
} from "@/components/form-items/MaskableTableSection";
import { BooleanBadge } from "@/components/ui/boolean-badge";
import {
  AddButton,
  CancelButton,
  DeleteButton,
  SaveButton,
  UpdateButton,
} from "@/components/ui/custom-buttons";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "@/components/ui/custom-dialog";
import { DataTable } from "@/components/ui/datatable";
import { Dialog, DialogClose, DialogHeader } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { RegisterVisibilityField } from "@/context/FormFieldVisibilityContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  exemplaireDansEstampesMultiplesHistoryDescriptionNom: z.string(),
  exemplaireDansEstampesMultiplesHistoryDescriptionVille: z.string(),
  exemplaireDansEstampesMultiplesHistoryDescriptionDates: z.string(),
  exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart:
    z.boolean(),
});

type ExemplaireHistoryForm = z.infer<typeof formSchema>;

const HISTORY_TABLE_MASK_GROUP = "exemplaireHistoriqueTableGroup";

/** Dialog inputs unmount when closed — keep them in the Champs drawer. */
const HISTORY_DIALOG_FIELDS = [
  "exemplaireDansEstampesMultiplesHistoryDescriptionNom",
  "exemplaireDansEstampesMultiplesHistoryDescriptionVille",
  "exemplaireDansEstampesMultiplesHistoryDescriptionDates",
] as const;

type CreateOrEditExemplaireHistoryProps = {
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  exemplaireHistory?: MultiplesampleHistory;
  exemplaireId: string;
};

export const CreateOrEditExemplaireHistory = ({
  disabled = false,
  onOpenChange,
  open,
  exemplaireHistory,
  exemplaireId,
}: CreateOrEditExemplaireHistoryProps) => {
  const isEdit = exemplaireHistory !== undefined;

  const updater = useEditMultiplesampleHistory();
  const creator = useCreateMultiplesampleHistory();
  const deleter = useDeleteMultiplesampleHistory();

  const { t } = useTranslation();

  const form = useForm<ExemplaireHistoryForm>({
    defaultValues: isEdit
      ? {
          exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart:
            exemplaireHistory.exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart,
          exemplaireDansEstampesMultiplesHistoryDescriptionDates:
            exemplaireHistory.exemplaireDansEstampesMultiplesHistoryDescriptionDates,
          exemplaireDansEstampesMultiplesHistoryDescriptionNom:
            exemplaireHistory.exemplaireDansEstampesMultiplesHistoryDescriptionNom,
          exemplaireDansEstampesMultiplesHistoryDescriptionVille:
            exemplaireHistory.exemplaireDansEstampesMultiplesHistoryDescriptionVille,
        }
      : {
          exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart:
            false,
          exemplaireDansEstampesMultiplesHistoryDescriptionDates: "",
          exemplaireDansEstampesMultiplesHistoryDescriptionNom: "",
          exemplaireDansEstampesMultiplesHistoryDescriptionVille: "",
        },
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (exemplaireHistory) {
      form.reset({
        exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart:
          exemplaireHistory.exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart,
        exemplaireDansEstampesMultiplesHistoryDescriptionDates:
          exemplaireHistory.exemplaireDansEstampesMultiplesHistoryDescriptionDates,
        exemplaireDansEstampesMultiplesHistoryDescriptionNom:
          exemplaireHistory.exemplaireDansEstampesMultiplesHistoryDescriptionNom,
        exemplaireDansEstampesMultiplesHistoryDescriptionVille:
          exemplaireHistory.exemplaireDansEstampesMultiplesHistoryDescriptionVille,
      });
    }
  }, [exemplaireHistory, form]);

  function onSubmit(values: ExemplaireHistoryForm) {
    if (isEdit && exemplaireHistory) {
      updater.mutate(
        {
          historyId: exemplaireHistory.id,
          exemplaireId: exemplaireHistory.multiplesampleId ?? exemplaireId,
          history: values,
        },
        { onSuccess: () => onOpenChange?.(false) },
      );
      return;
    }

    creator.mutate(
      { history: values, exemplaireId },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange?.(false);
        },
      },
    );
  }

  function onDelete() {
    if (!isEdit || !exemplaireHistory) return;
    deleter.mutate(
      {
        exemplaireId: exemplaireHistory.multiplesampleId ?? exemplaireId,
        historyId: exemplaireHistory.id,
      },
      {
        onSuccess: () => onOpenChange?.(false),
      },
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <CustomDialogContent>
        <Form {...form}>
          <form
            className="flex flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <DialogHeader>
              <CustomDialogTitle>{t("addArtworkHistory")}</CustomDialogTitle>
            </DialogHeader>
            <AF.Col className="p-6">
              <AF.Text name="exemplaireDansEstampesMultiplesHistoryDescriptionNom" />
              <AF.Text name="exemplaireDansEstampesMultiplesHistoryDescriptionVille" />
              <AF.Text name="exemplaireDansEstampesMultiplesHistoryDescriptionDates" />
              <AF.Checkbox name="exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart" />
              {isEdit && (
                <DeleteButton
                  variant={"link"}
                  className="text-red-600 w-fit m-auto"
                  iconProps={{ className: "stroke-red-600" }}
                  onClick={onDelete}
                  disabled={disabled}
                >
                  {t("supprimerOeuvresLocalisation")}
                </DeleteButton>
              )}
            </AF.Col>
            <CustomDialogFooter>
              <DialogClose asChild>
                <CancelButton className="flex-1" />
              </DialogClose>
              {isEdit ? (
                <UpdateButton
                  type="submit"
                  className="flex-1"
                  disabled={disabled}
                />
              ) : (
                <SaveButton
                  type="submit"
                  className="flex-1"
                  disabled={disabled}
                />
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
};

export const ExemplaireHistoryTable = ({
  exemplaireId,
  disabled = false,
  provenanceMode = false,
}: {
  exemplaireId?: string;
  disabled?: boolean;
  provenanceMode?: boolean;
}) => {
  const { t } = useTranslation();

  type HistoryRow =
    | (MultiplesampleHistory & { kind: "history" })
    | (MultiplesampleHistoryMergedAuction & { kind: "auction" });

  const isHistoryRow = (
    row: HistoryRow,
  ): row is MultiplesampleHistory & { kind: "history" } =>
    row.kind === "history";

  const { data: historyOnly = [] } = useGetHistoryOfExemplaire(
    provenanceMode ? undefined : exemplaireId,
  );
  const { data: merged } = useGetHistoryOfExemplaireMerged(
    exemplaireId,
    provenanceMode,
  );

  const [rows, setRows] = useState<HistoryRow[]>([]);

  const [openEditForm, setOpenEditForm] = useState(false);
  const [openCreateForm, setOpenCreateForm] = useState(false);

  const historyToUpdate = useRef<MultiplesampleHistory>();

  const sequenceMutator = useUpdateMultiplesampleHistorySequence();
  const provenanceReorderMutator = useReorderMultiplesampleProvenance();

  function handleUpdate(exemplaireHistory: MultiplesampleHistory) {
    historyToUpdate.current = exemplaireHistory;
    setOpenEditForm(true);
  }

  const datatableColumns = useMemo(
    (): ColumnDef<HistoryRow>[] => [
      {
        size: 1000,
        header: t("tab_exemplaireHistoriqueNom"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) {
            return r.exemplaireDansEstampesMultiplesHistoryDescriptionNom;
          }
          const parts = [
            r.auction?.ventesDescriptionTitre,
            r.multiplesampleDansVentesDescriptionNumeroLot
              ? `lot n° ${r.multiplesampleDansVentesDescriptionNumeroLot}`
              : null,
          ].filter(Boolean);
          return parts.length ? parts.join(" - ") : t("entryauctions");
        },
      },
      {
        header: t("tab_exemplaireHistoriqueVille"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) {
            return r.exemplaireDansEstampesMultiplesHistoryDescriptionVille;
          }
          const parts = [
            r.auction?.ventesDescriptionLieu,
            r.auction?.ventesDescriptionVille,
            r.auction?.ventesDescriptionPays,
          ].filter(Boolean);
          return parts.join(", ");
        },
      },
      {
        header: t("tab_exemplaireHistoriqueDates"),
        cell: ({ row }) => {
          const r = row.original;
          if (isHistoryRow(r)) {
            return r.exemplaireDansEstampesMultiplesHistoryDescriptionDates;
          }
          return r.auction?.ventesDescriptionDate ?? "";
        },
      },
      {
        header: t("tab_exemplaireHistoriqueCollectionParticuliere"),
        cell: (v) => (
          <BooleanBadge
            value={
              isHistoryRow(v.row.original)
                ? v.row.original
                    .exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart
                : false
            }
          />
        ),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (provenanceMode && merged) {
      setRows([...merged.timeline]);
      return;
    }
    setRows(
      historyOnly.map<HistoryRow>((h) => ({ ...h, kind: "history" as const })),
    );
  }, [historyOnly, merged, provenanceMode]);

  const handleRowDragged = (newData: HistoryRow[]) => {
    if (!exemplaireId) return;
    setRows(newData);
    if (provenanceMode) {
      provenanceReorderMutator.mutate({
        exemplaireId,
        timeline: newData.map((d) => ({ kind: d.kind, id: d.id })),
      });
      return;
    }
    sequenceMutator.mutate({
      newSequence: newData.filter(isHistoryRow).map((d) => d.id),
      exemplaireId,
    });
  };

  const titleLabel = t(
    provenanceMode ? "exemplaireTabProvenance" : "exemplaireTabHistorique",
  );

  const addButton = (
    <AddButton
      type="button"
      onClick={() => setOpenCreateForm(true)}
      disabled={disabled}
    />
  );

  if (!exemplaireId) {
    return null;
  }

  return (
    <>
      {HISTORY_DIALOG_FIELDS.map((name) => (
        <RegisterVisibilityField key={name} name={name} label={t(name)} />
      ))}
      <MaskableTableSection>
        <CreateOrEditExemplaireHistory
          exemplaireId={exemplaireId}
          exemplaireHistory={historyToUpdate.current}
          open={openEditForm}
          onOpenChange={setOpenEditForm}
          disabled={disabled}
        />
        <CreateOrEditExemplaireHistory
          open={openCreateForm}
          onOpenChange={setOpenCreateForm}
          exemplaireId={exemplaireId}
          disabled={disabled}
        />
        <MaskableTableSectionHeader
          groupName={HISTORY_TABLE_MASK_GROUP}
          label={titleLabel}
          trailing={addButton}
        />
        <MaskableTableSectionBody groupName={HISTORY_TABLE_MASK_GROUP}>
          <DataTable
            enableDragRows
            onRowDragged={(d) => handleRowDragged(d.newData)}
            columns={datatableColumns}
            data={rows}
            onRowClick={(e) => {
              if (isHistoryRow(e.original)) handleUpdate(e.original);
            }}
          />
        </MaskableTableSectionBody>
      </MaskableTableSection>
    </>
  );
};
